/* eslint-disable react-hooks/exhaustive-deps */
import {
  ActionCardProps,
  ActionsProps,
  Product,
  RightPanelTabs,
  RightPanelTabsConfiguration,
} from '@access-erp/common';
import { CardType, IRowNode } from '@access-erp/ui-components';
import { useEffect, useMemo, useState } from 'react';

import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import { UsageHeaderOutput } from '@/graphql/contracts/_generated_/contracts';

type ActionButton = {
  config: ActionCardProps;
  isVisible: boolean;
};

interface UseRightPanelParams {
  selectedHeaderRows: IRowNode<UsageHeaderOutput>[];
  resetCreateUsageHeader: () => void;
}

export function useRightPanel({ selectedHeaderRows, resetCreateUsageHeader }: UseRightPanelParams) {
  const [isOpenFileUploadWidget, setIsOpenFileUploadWidget] = useState(false);
  const [isVisibleDeleteConfirmModal, setIsVisibleDeleteConfirmModal] = useState(false);
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };
    getRole();
  }, [getUserRole]);

  const isReaderRole = userRole === UserRole.READER;
  const isUserRole = userRole === UserRole.USER;
  const isRestrictedRole = isUserRole || isReaderRole;

  const handleFileUploadWidgetOpen = () => {
    setIsOpenFileUploadWidget(true);
    resetCreateUsageHeader();
  };

  const actionButtons = useMemo<ActionButton[]>(
    () => [
      {
        config: {
          text: 'Upload file',
          cardType: CardType.ACTION,
          product: Product.ContractsAndBilling,
          onClick: () => handleFileUploadWidgetOpen(),
        },
        isVisible: !isRestrictedRole,
      },
      {
        config: {
          text: 'Delete',
          cardType: CardType.DELETE,
          product: Product.ContractsAndBilling,
          onClick: () => setIsVisibleDeleteConfirmModal(true),
        },
        isVisible: !isRestrictedRole && selectedHeaderRows.length > 0,
      },
    ],
    [selectedHeaderRows, isRestrictedRole],
  );

  const actionsParams: ActionsProps = {
    actionCards: {
      actions: actionButtons.filter((el) => el.isVisible).map((el) => el.config),
    },
  };

  const tabsConfiguration = useMemo<RightPanelTabsConfiguration>(
    () => ({
      [RightPanelTabs.ACTIONS]: actionsParams,
    }),
    [selectedHeaderRows, actionButtons],
  );

  const closeWidget = () => setIsOpenFileUploadWidget(false);

  return {
    isOpenFileUploadWidget,
    closeWidget,
    closeDeleteConfirmModal: () => setIsVisibleDeleteConfirmModal(false),
    tabsConfiguration,
    isVisibleDeleteConfirmModal,
  };
}
